import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/Reveal";
import { SERVICES } from "@/data/site";
import { Link } from "react-router-dom";

export default function ServicesSection() {
  const [openId, setOpenId] = useState(SERVICES[0].id);

  return (
    <section
      id="services"
      className="relative py-24 md:py-32 ink-grain"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <span className="eyebrow">What we do</span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2
            className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl mt-5 max-w-5xl"
            data-testid="services-heading"
          >
            Five disciplines. <br />
            <span className="text-stroke">One ruthless</span>{" "}
            <span className="text-[#FF4500]">growth engine.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.18}>
          <p className="mt-6 max-w-2xl text-white/70 text-lg">
            We don't sell deliverables. We sell outcomes — built end-to-end so
            nothing falls between the cracks.
          </p>
        </Reveal>

        <div className="mt-14 border-t border-white/10">
          {SERVICES.map((s) => {
            const isOpen = openId === s.id;
            return (
              <Reveal key={s.id} className="border-b border-white/10">
                <button
                  onClick={() => setOpenId(isOpen ? null : s.id)}
                  className="svc-row w-full grid grid-cols-12 items-center gap-4 py-7 text-left"
                  data-testid={`service-row-${s.id}`}
                >
                  <span className="col-span-2 md:col-span-1 font-mono text-xs md:text-sm text-[#FF4500] tracking-widest">
                    {s.number}
                  </span>
                  <span className="col-span-7 md:col-span-7 font-display text-3xl md:text-5xl uppercase font-bold tracking-tight leading-none">
                    {s.title}
                  </span>
                  <span className="hidden md:block md:col-span-3 text-sm text-white/55 font-medium">
                    {s.tagline}
                  </span>
                  <span
                    className={`col-span-3 md:col-span-1 justify-self-end inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 transition-transform duration-300 ${
                      isOpen ? "rotate-45 bg-[#FF4500] border-[#FF4500] text-white" : ""
                    }`}
                  >
                    <Plus className="h-4 w-4" />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.25, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-10 pt-2">
                        <div className="lg:col-span-5">
                          <p className="text-white/85 text-base md:text-lg leading-relaxed">
                            {s.description}
                          </p>
                          <Link
                            to="/services"
                            className="link-underline inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] mt-6 text-[#FF4500] font-semibold"
                            data-testid={`service-cta-${s.id}`}
                          >
                            Explore service <ArrowUpRight className="h-4 w-4" />
                          </Link>
                        </div>
                        <div className="lg:col-span-4">
                          <div className="text-xs uppercase tracking-[0.24em] text-white/45 mb-3">
                            What you get
                          </div>
                          <ul className="space-y-2">
                            {s.deliverables.map((d) => (
                              <li
                                key={d}
                                className="flex items-center gap-2 text-white/85"
                              >
                                <span className="h-1 w-1 rounded-full bg-[#FF4500]" />
                                {d}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="lg:col-span-3">
                          <div className="text-xs uppercase tracking-[0.24em] text-white/45 mb-3">
                            Stack
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {s.stack.map((t) => (
                              <span key={t} className="float-tag">
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
